import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiCheckCircle, FiAlertCircle, FiLock, FiUser } from 'react-icons/fi';
import { getApiBase } from '../apiBase'; // Adjust path as needed

const AcceptInvitePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    password: '',
    confirm_password: '',
    fullname: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const token = searchParams.get('token');

  // If no token is present, show an error immediately
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F7F3F7] via-[#F0E6F5] to-[#EDE3F0]">
        <div className="bg-white p-8 rounded-2xl shadow-xl text-center max-w-md">
          <FiAlertCircle size={48} className="text-rose-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-[#5B3A60] mb-2">Invalid Link</h2>
          <p className="text-[#6B7280]">This invitation link is invalid or missing a token.</p>
          <button 
            onClick={() => navigate('/login')}
            className="mt-6 px-6 py-2 bg-[#5B3A60] text-white rounded-xl hover:bg-[#6B4A70] transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirm_password) {
      setError('Passwords do not match');
      return;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${getApiBase()}/team/accept-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token,
          password: formData.password,
          confirm_password: formData.confirm_password,
          fullname: formData.fullname
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Failed to accept invitation');
      }

      setSuccess('Account activated successfully! Redirecting to login...');
      setTimeout(() => {
        navigate('/login');
      }, 2000);

    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F7F3F7] via-[#F0E6F5] to-[#EDE3F0] p-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white w-full max-w-md p-8 rounded-2xl shadow-2xl border border-[#B89D4F]/30"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-[#B89D4F] to-[#A88D3F] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <FiUser className="text-white text-2xl" />
          </div>
          <h1 className="text-2xl font-bold text-[#5B3A60]">Complete Your Registration</h1>
          <p className="text-[#6B7280] mt-2">You've been invited to join a team. Set your password to get started.</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-xl flex items-center gap-3 text-rose-700">
            <FiAlertCircle size={20} />
            <span className="text-sm font-medium">{error}</span>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3 text-emerald-700">
            <FiCheckCircle size={20} />
            <span className="text-sm font-medium">{success}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-[#5B3A60] mb-2">Full Name</label>
            <div className="relative">
              <FiUser className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A89CA5]" />
              <input
                type="text"
                required
                value={formData.fullname}
                onChange={(e) => setFormData({...formData, fullname: e.target.value})}
                className="w-full pl-11 pr-4 py-3 rounded-xl border border-[#A89CA5]/30 focus:border-[#5B3A60] focus:ring-2 focus:ring-[#B89D4F]/20 outline-none transition-all"
                placeholder="John Doe"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#5B3A60] mb-2">Password</label>
            <div className="relative">
              <FiLock className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A89CA5]" />
              <input
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="w-full pl-11 pr-4 py-3 rounded-xl border border-[#A89CA5]/30 focus:border-[#5B3A60] focus:ring-2 focus:ring-[#B89D4F]/20 outline-none transition-all"
                placeholder="••••••••"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#5B3A60] mb-2">Confirm Password</label>
            <div className="relative">
              <FiLock className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A89CA5]" />
              <input
                type="password"
                required
                value={formData.confirm_password}
                onChange={(e) => setFormData({...formData, confirm_password: e.target.value})}
                className="w-full pl-11 pr-4 py-3 rounded-xl border border-[#A89CA5]/30 focus:border-[#5B3A60] focus:ring-2 focus:ring-[#B89D4F]/20 outline-none transition-all"
                placeholder="••••••••"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || !!success}
            className="w-full py-3 bg-gradient-to-r from-[#5B3A60] to-[#6B4A70] text-white font-semibold rounded-xl shadow-lg shadow-[#5B3A60]/20 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Activating...' : 'Activate Account'}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default AcceptInvitePage;